import { useEffect, useState } from 'react'
import { Shield, Eye, EyeOff, Check, X, ChevronRight, Plus } from 'lucide-react'
import {
  useACLSettings,
  useACLCategories,
  useCreateACLCategory,
  useShareRequests,
  useShareRequestActions,
  useUpdateACLSettings,
} from '../hooks/useAcl'
import type { DefaultACLSettings } from '../types/api'

const DEFAULT_CATEGORY_RULES = [
  { role: 'self', permission: 'full' as const, requireApproval: false },
  { role: 'manager', permission: 'summary' as const, requireApproval: true },
  { role: 'team', permission: 'none' as const, requireApproval: false },
]

function accessLabel(access: 'full' | 'summary' | 'none') {
  if (access === 'full') return '全て閲覧可'
  if (access === 'summary') return '承認後の要約のみ'
  return '閲覧不可'
}

function accessTone(access: 'full' | 'summary' | 'none') {
  if (access === 'full') return 'text-green-600'
  if (access === 'summary') return 'text-yellow-600'
  return 'text-muted-foreground'
}

export function AclPage() {
  const settingsQuery = useACLSettings()
  const categoriesQuery = useACLCategories()
  const requestsQuery = useShareRequests()
  const updateSettings = useUpdateACLSettings()
  const createCategory = useCreateACLCategory()
  const {
    approve,
    reject,
    isLoading: isMutatingRequest,
    approveError,
    rejectError,
  } = useShareRequestActions()

  const [settingsForm, setSettingsForm] = useState<DefaultACLSettings>({
    defaultVisibility: 'private',
    requireApprovalForSharing: true,
    shareSummaryOnly: true,
  })
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryIcon, setNewCategoryIcon] = useState('📁')

  useEffect(() => {
    if (settingsQuery.data) {
      setSettingsForm(settingsQuery.data)
    }
  }, [settingsQuery.data])

  const saveSettings = async () => {
    await updateSettings.mutateAsync(settingsForm)
  }

  const createDefaultCategory = async () => {
    const trimmed = newCategoryName.trim()
    if (!trimmed) return

    await createCategory.mutateAsync({
      name: trimmed,
      icon: newCategoryIcon.trim() || '📁',
      rules: DEFAULT_CATEGORY_RULES,
    })

    setNewCategoryName('')
    setNewCategoryIcon('📁')
    setShowCreateForm(false)
  }

  const categories = categoriesQuery.data ?? []
  const pendingRequests = requestsQuery.data ?? []
  const settingsError = settingsQuery.error instanceof Error ? settingsQuery.error.message : null
  const categoriesError = categoriesQuery.error instanceof Error ? categoriesQuery.error.message : null
  const requestsError = requestsQuery.error instanceof Error ? requestsQuery.error.message : null
  const actionError = updateSettings.error instanceof Error
    ? updateSettings.error.message
    : createCategory.error instanceof Error
      ? createCategory.error.message
      : null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">コンテキスト共有範囲管理</h1>
        <p className="text-muted-foreground">
          あなたのコンテキストを誰にどこまで見せるかを管理します
        </p>
      </div>

      <div className="flex items-center gap-3 rounded-lg bg-primary/5 p-4 text-primary">
        <Shield className="h-5 w-5" />
        <p>設定は現在のワークスペースに対して保存されます</p>
      </div>

      <div className="rounded-lg border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold">デフォルト共有設定</h2>
          <button
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            onClick={() => void saveSettings()}
            disabled={updateSettings.isPending || settingsQuery.isLoading}
          >
            {updateSettings.isPending ? '保存中...' : '保存'}
          </button>
        </div>

        {settingsError && (
          <p className="mb-4 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            設定取得に失敗しました: {settingsError}
          </p>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="acl-default-visibility" className="text-sm font-medium">
              新規コンテキスト
            </label>
            <select
              id="acl-default-visibility"
              className="mt-1 w-full rounded-md border bg-background px-3 py-2"
              value={settingsForm.defaultVisibility}
              onChange={(event) => {
                const value = event.target.value as DefaultACLSettings['defaultVisibility']
                setSettingsForm((current) => ({ ...current, defaultVisibility: value }))
              }}
            >
              <option value="private">自分のみ（推奨）</option>
              <option value="team">チーム全員</option>
              <option value="organization">部署全員</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">承認フロー</label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="require-approval"
                checked={settingsForm.requireApprovalForSharing}
                onChange={(event) => {
                  setSettingsForm((current) => ({
                    ...current,
                    requireApprovalForSharing: event.target.checked,
                  }))
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="require-approval" className="text-sm">
                上司への共有前に承認を求める
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="summary-only"
                checked={settingsForm.shareSummaryOnly}
                onChange={(event) => {
                  setSettingsForm((current) => ({
                    ...current,
                    shareSummaryOnly: event.target.checked,
                  }))
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="summary-only" className="text-sm">
                要約のみを共有（詳細は非公開）
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="font-semibold">カテゴリ別設定</h2>
            {categoriesError && (
              <p className="mt-1 text-sm text-destructive">カテゴリ取得に失敗しました: {categoriesError}</p>
            )}
          </div>
          <button
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            onClick={() => setShowCreateForm((current) => !current)}
          >
            <Plus className="h-4 w-4" />
            新規追加
          </button>
        </div>

        {showCreateForm && (
          <div className="border-b bg-muted/30 px-6 py-4">
            <div className="grid gap-3 md:grid-cols-[120px_1fr_auto]">
              <div className="space-y-1">
                <label htmlFor="acl-category-icon" className="text-xs font-medium text-muted-foreground">
                  アイコン
                </label>
                <input
                  id="acl-category-icon"
                  className="w-full rounded-md border bg-background px-3 py-2"
                  value={newCategoryIcon}
                  onChange={(event) => setNewCategoryIcon(event.target.value)}
                  placeholder="📁"
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="acl-category-name" className="text-xs font-medium text-muted-foreground">
                  新規カテゴリ名
                </label>
                <input
                  id="acl-category-name"
                  className="w-full rounded-md border bg-background px-3 py-2"
                  value={newCategoryName}
                  onChange={(event) => setNewCategoryName(event.target.value)}
                  placeholder="カテゴリ名"
                />
              </div>
              <button
                className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                onClick={() => void createDefaultCategory()}
                disabled={createCategory.isPending || newCategoryName.trim().length === 0}
              >
                {createCategory.isPending ? '追加中...' : '追加'}
              </button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              新規カテゴリは「自分=全文」「manager=要約」「team=非公開」の初期ルールで作成されます。
            </p>
          </div>
        )}

        <div className="divide-y">
          {categoriesQuery.isLoading && (
            <div className="p-6 text-sm text-muted-foreground">カテゴリを読み込み中です...</div>
          )}
          {!categoriesQuery.isLoading && categories.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground">カテゴリはまだありません。</div>
          )}
          {categories.map((category) => (
            <div key={category.id} className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{category.icon ?? '📁'}</span>
                  <h3 className="font-medium">{category.name}</h3>
                </div>
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  ルール {category.rules.length} 件
                  <ChevronRight className="h-4 w-4" />
                </span>
              </div>
              <div className="mt-4 space-y-2 pl-9">
                {category.rules.map((rule, index) => (
                  <div key={`${category.id}-${rule.role}-${index}`} className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">├──</span>
                    {rule.permission === 'none' ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className={`h-4 w-4 ${rule.permission === 'full' ? 'text-green-600' : 'text-yellow-600'}`} />
                    )}
                    <span>{rule.role}:</span>
                    <span className={accessTone(rule.permission)}>
                      {accessLabel(rule.permission)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border bg-card shadow-sm">
        <div className="border-b px-6 py-4">
          <h2 className="font-semibold">承認待ち共有リクエスト</h2>
          {requestsError && (
            <p className="mt-1 text-sm text-destructive">リクエスト取得に失敗しました: {requestsError}</p>
          )}
        </div>

        {requestsQuery.isLoading && (
          <div className="p-6 text-sm text-muted-foreground">リクエストを読み込み中です...</div>
        )}

        {!requestsQuery.isLoading && pendingRequests.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">承認待ちリクエストはありません。</div>
        )}

        {(approveError instanceof Error || rejectError instanceof Error) && (
          <div className="border-b px-6 py-3">
            <p className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {approveError instanceof Error
                ? `承認に失敗しました: ${approveError.message}`
                : rejectError instanceof Error
                  ? `却下に失敗しました: ${rejectError.message}`
                  : null}
            </p>
          </div>
        )}

        <div className="divide-y">
          {pendingRequests.map((request) => (
            <div key={request.id} className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-medium">{request.documentId}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    リクエスト元: {request.requester.name} ({request.requester.role})
                  </p>
                  <p className="text-sm text-muted-foreground">
                    権限: {request.requestedPermission === 'full' ? '全文共有' : '要約共有'}
                  </p>
                  {request.reason && (
                    <p className="text-sm text-muted-foreground">理由: {request.reason}</p>
                  )}
                  <p className="mt-2 rounded-md bg-muted px-3 py-2 text-sm">{request.preview}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    className="flex items-center gap-1 rounded-md bg-green-600 px-3 py-1 text-sm text-white hover:bg-green-700 disabled:opacity-60"
                    onClick={() => void approve({ requestId: request.id })}
                    disabled={isMutatingRequest}
                  >
                    <Check className="h-4 w-4" />
                    承認
                  </button>
                  <button
                    className="flex items-center gap-1 rounded-md bg-destructive px-3 py-1 text-sm text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
                    onClick={() => void reject({ requestId: request.id })}
                    disabled={isMutatingRequest}
                  >
                    <X className="h-4 w-4" />
                    却下
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {actionError && (
        <p className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          操作に失敗しました: {actionError}
        </p>
      )}
    </div>
  )
}
